import React, { useState, useMemo, useEffect } from "react";
import Modal from "./Modal";
import PrimaryButton from "../components/Buttons/PrimaryButton";
import SecondaryButton from "../components/Buttons/SecondaryButton";
import { Search, X, Loader2 } from "lucide-react";
import { getDocumentShares, removeDocumentShare } from "../utils/Api";
import { useSendEmail } from "../hooks/useSendEmail";
import { useAuth } from "../utils/AuthContext";
import { documentShareNotificationEmail } from "../emails";

interface Member {
  id: string;
  user_id: string | null;
  db_user_id: string | null;
  email: string;
  name?: string;
  role: string;
  status: string;
}

interface DocumentShare {
  id: string;
  document_id: string;
  shared_with_user_id: string;
  shared_with_email: string;
  shared_with_name?: string;
  shared_by_user_id?: string;
  created_at: string;
}

interface ShareDocumentModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  document: Record<string, any> | null;
  members: Member[];
  onShare: (userIds: string[]) => Promise<void>;
}

const ShareDocumentModal: React.FC<ShareDocumentModalProps> = ({
  isOpen,
  setIsOpen,
  document,
  members,
  onShare,
}) => {
  const { user } = useAuth();
  const { sendEmail } = useSendEmail();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(
    new Set(),
  );
  const [existingShares, setExistingShares] = useState<DocumentShare[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch existing shares when modal opens
  useEffect(() => {
    let cancelled = false;

    const fetchExistingShares = async () => {
      if (!document?.id) return;
      setIsLoading(true);
      setError(null);
      try {
        const response = await getDocumentShares(document.id);
        if (!cancelled) {
          setExistingShares(response.data.shares || []);
        }
      } catch (err: any) {
        if (!cancelled) {
          if (err.response?.status !== 403) {
            setError("Failed to load existing shares");
          }
          setExistingShares([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    if (isOpen && document?.id) {
      fetchExistingShares();
    }

    return () => {
      cancelled = true;
    };
  }, [isOpen, document?.id]);

  const fetchExistingShares = async () => {
    if (!document?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await getDocumentShares(document.id);
      setExistingShares(response.data.shares || []);
    } catch (err: any) {
      if (err.response?.status !== 403) {
        setError("Failed to load existing shares");
      }
      setExistingShares([]);
    } finally {
      setIsLoading(false);
    }
  };

  const activeMembersOnly = members.filter(
    (m) => m.status !== "invited" && m.db_user_id,
  );

  const sharedUserIds = new Set(
    existingShares.map((s) => s.shared_with_user_id),
  );
  const availableMembers = activeMembersOnly.filter(
    (m) =>
      m.db_user_id &&
      !sharedUserIds.has(m.db_user_id) &&
      m.user_id !== document?.user_id &&
      m.role.toLowerCase() !== "admin",
  );

  const filteredMembers = useMemo(() => {
    if (!searchQuery.trim()) return availableMembers;
    const query = searchQuery.toLowerCase();
    return availableMembers.filter(
      (member) =>
        (member.name?.toLowerCase() || "").includes(query) ||
        member.email.toLowerCase().includes(query),
    );
  }, [availableMembers, searchQuery]);

  const toggleMemberSelection = (memberId: string) => {
    const newSelected = new Set(selectedMemberIds);
    if (newSelected.has(memberId)) {
      newSelected.delete(memberId);
    } else {
      newSelected.add(memberId);
    }
    setSelectedMemberIds(newSelected);
  };

  const handleShare = async () => {
    const selectedMembersList = Array.from(selectedMemberIds)
      .map((memberId) => members.find((m) => m.id === memberId))
      .filter((m): m is Member => m !== undefined);

    const userIds = selectedMembersList
      .map((member) => member.db_user_id)
      .filter((id): id is string => id !== null && id !== undefined);

    if (userIds.length === 0) return;

    setIsSharing(true);
    setError(null);
    try {
      await onShare(userIds);

      const documentUrl = `${window.location.origin}/view/${document?.id}`;
      const sharedByName =
        user?.name || user?.displayName || user?.email || "Someone";
      const documentName = document?.name || "Untitled Document";

      await Promise.all(
        selectedMembersList.map((member) =>
          sendEmail({
            to: member.email,
            template: documentShareNotificationEmail({
              documentName,
              sharedByName,
              documentUrl,
            }),
          }),
        ),
      );

      setSelectedMemberIds(new Set());
      await fetchExistingShares();
    } catch (err: any) {
      setError(err.message || "Failed to share document");
    } finally {
      setIsSharing(false);
    }
  };

  const handleRemoveShare = async (shareId: string) => {
    if (!document?.id) return;
    setError(null);
    try {
      await removeDocumentShare(document.id, shareId);
      setExistingShares((prev) => prev.filter((s) => s.id !== shareId));
    } catch (err: any) {
      setError(err.message || "Failed to remove share");
    }
  };

  const handleClose = () => {
    setSearchQuery("");
    setSelectedMemberIds(new Set());
    setExistingShares([]);
    setError(null);
    setIsOpen(false);
  };

  const selectedMembers = useMemo(() => {
    return Array.from(selectedMemberIds)
      .map((id) => members.find((m) => m.id === id))
      .filter((m): m is Member => m !== undefined);
  }, [selectedMemberIds, members]);

  if (!document) return null;

  return (
    <Modal isOpen={isOpen} setIsOpen={handleClose}>
      <div className="w-full">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Share Document
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Share "{document.name || "Untitled Document"}" with organisation
          members
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Left Column - Available Members */}
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Organisation Members
              </h4>

              {/* Search Input */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search members..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1d365d] focus:border-transparent text-sm"
                />
              </div>

              {/* Members List */}
              <div className="h-48 sm:h-64 overflow-y-auto border border-gray-200 rounded-md">
                {availableMembers.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-gray-500 text-sm p-4 text-center">
                    {existingShares.length > 0
                      ? "All members already have access."
                      : "No members available to share with."}
                  </div>
                ) : filteredMembers.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-gray-500 text-sm p-4 text-center">
                    No members found matching "{searchQuery}"
                  </div>
                ) : (
                  <div className="p-2 space-y-1">
                    {filteredMembers.map((member) => (
                      <button
                        key={member.id}
                        onClick={() => toggleMemberSelection(member.id)}
                        className={`w-full flex items-center p-2 rounded-md transition-colors text-left ${
                          selectedMemberIds.has(member.id)
                            ? "bg-blue-50 border border-blue-200"
                            : "hover:bg-gray-50 border border-transparent"
                        }`}
                      >
                        <div className="flex-shrink-0 h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center mr-3">
                          <span className="text-sm font-medium text-gray-600">
                            {(member.name || member.email)
                              .charAt(0)
                              .toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {member.name || "—"}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {member.email}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Selected Members */}
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Shared With ({existingShares.length + selectedMembers.length})
              </h4>

              <div className="h-48 sm:h-[calc(64px+12px+256px)] overflow-y-auto border border-gray-200 rounded-md">
                {existingShares.length === 0 && selectedMembers.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-gray-500 text-sm p-4 text-center">
                    Select members from the left to share this document
                  </div>
                ) : (
                  <div className="p-2 space-y-1">
                    {/* Existing Shares */}
                    {existingShares.map((share) => (
                      <div
                        key={share.id}
                        className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded-md"
                      >
                        <div className="flex items-center min-w-0 flex-1">
                          <div className="flex-shrink-0 h-8 w-8 bg-green-200 rounded-full flex items-center justify-center mr-3">
                            <span className="text-sm font-medium text-green-700">
                              {(
                                share.shared_with_name ||
                                share.shared_with_email
                              )
                                .charAt(0)
                                .toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {share.shared_with_name ||
                                share.shared_with_email}
                            </p>
                            {share.shared_with_name && (
                              <p className="text-xs text-gray-500 truncate">
                                {share.shared_with_email}
                              </p>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveShare(share.id)}
                          className="ml-2 p-1 text-gray-400 hover:text-red-500 transition-colors"
                          title="Remove access"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}

                    {/* Newly Selected Members */}
                    {selectedMembers.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded-md"
                      >
                        <div className="flex items-center min-w-0 flex-1">
                          <div className="flex-shrink-0 h-8 w-8 bg-blue-200 rounded-full flex items-center justify-center mr-3">
                            <span className="text-sm font-medium text-blue-700">
                              {(member.name || member.email)
                                .charAt(0)
                                .toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {member.name || "—"}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {member.email}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => toggleMemberSelection(member.id)}
                          className="ml-2 p-1 text-gray-400 hover:text-red-500 transition-colors"
                          title="Remove"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 mt-2">
          <PrimaryButton
            onClick={handleShare}
            disabled={selectedMemberIds.size === 0 || isSharing}
            className="flex-1"
          >
            {isSharing ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Sharing...
              </span>
            ) : (
              `Share${selectedMemberIds.size > 0 ? ` (${selectedMemberIds.size})` : ""}`
            )}
          </PrimaryButton>
          <SecondaryButton onClick={handleClose} className="flex-1">
            Close
          </SecondaryButton>
        </div>
      </div>
    </Modal>
  );
};

export default ShareDocumentModal;
